import { useCallback, useRef, useState } from 'react';

import moment from 'moment';

import {
  CalendarAssignmentEmployee,
  PersonAbsence,
  Shift,
  ShiftInterfere,
  TaskForCalendarAssignment,
  TreeNode
} from '../../../types/interfaces';
import { generateGUID } from '../../../utils';

import { RuleValidationError } from './useCalendarAssignmentsApi';

export type SelectedTaskInfo = {
  personId: number;
  day: string;
  tasks: TaskForCalendarAssignment[];
} | null;

export type DateRangeEmployeesParams = {
  startDate: Date;
  endDate: Date;
  selectedEmployees: CalendarAssignmentEmployee[];
} | null;

export type RecalcParams = {
  startDate: Date;
  endDate: Date;
  selectedEmployees: CalendarAssignmentEmployee[];
  shouldClear: boolean;
  shouldDeleteAbsences: boolean;
} | null;

export interface UseCalendarAssignmentsDialogsStateProps {
  selectedDepartmentFilter: TreeNode | null;
  selectedDateFilter: Date | null;
}

export function useCalendarAssignmentsDialogsState(
  props: UseCalendarAssignmentsDialogsStateProps
) {
  const { selectedDepartmentFilter, selectedDateFilter } = props;

  const [selectedTaskInfo, setSelectedTaskInfo] = useState<SelectedTaskInfo>(
    null
  );
  const [absenceDialogVisible, setAbsenceDialogVisible] = useState(false);
  const [absenceData, setAbsenceData] = useState<PersonAbsence | null>(null);
  const [choiceDialogVisible, setChoiceDialogVisible] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const [validationErrors, setValidationErrors] = useState<ShiftInterfere[]>(
    []
  );
  const [validationErrorsImport, setValidationErrorsImport] = useState<
    { error: string }[]
  >([]);
  const [errorDialogVisible, setErrorDialogVisible] = useState<boolean>(false);
  const [validationDialogVisible, setValidationDialogVisible] = useState(false);
  const [ruleValidationErrors, setRuleValidationErrors] = useState<
    RuleValidationError[]
  >([]);
  const [ruleValidationDialogVisible, setRuleValidationDialogVisible] =
    useState(false);

  const [deletionDialogVisible, setDeletionDialogVisible] = useState(false);
  const [deletionAbsenceDialogVisible, setDeletionAbsenceDialogVisible] =
    useState(false);
  const [
    confirmAbsenceDeletionDialogVisible,
    setConfirmAbsenceDeletionDialogVisible
  ] = useState(false);
  const [confirmDeletionDialogVisible, setConfirmDeletionDialogVisible] =
    useState(false);

  const [deletionAbsenceParams, setDeletionAbsenceParams] =
    useState<DateRangeEmployeesParams>(null);
  const [recalcParams, setRecalcParams] = useState<RecalcParams>(null);
  const [deletionParams, setDeletionParams] = useState<DateRangeEmployeesParams>(
    null
  );

  const [shiftDialogVisible, setShiftDialogVisible] = useState(false);
  const [shiftData, setShiftData] = useState<Shift | null>(null);
  const [confirmRecalcDialogVisible, setConfirmRecalcDialogVisible] =
    useState(false);

  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [calculatorDialogVisible, setCalculatorDialogVisible] = useState(false);
  const [unassignedTasksDialogVisible, setUnassignedTasksDialogVisible] =
    useState(false);
  const [unassignedTasksCount, setUnassignedTasksCount] = useState<number>(0);

  const calendarCardRef = useRef<null | { handleCancel: () => void }>(null);
  const absenceCardRef = useRef<null | { handleCancel: () => void }>(null);

  const openNewDialog = useCallback(
    (type: 'shift' | 'absence' | 'task', personId?: number, date?: moment.Moment) => {
      const finalDate = (() => {
        if (date && date.isValid()) return date.clone(); // Используем переданный date, если валиден
        const selected = moment(selectedDateFilter);
        if (selected.isValid()) return selected; // Используем selectedDateFilter, если валиден
        return moment(); // Fallback на текущую дату
      })();

      const commonData = {
        id: 0,
        personId: personId || 0,
        date: finalDate.format('YYYY-MM-DD'),
        guid: `temp-${generateGUID()}`,
        departmentId: Number(selectedDepartmentFilter?.key)
      };

      if (type === 'shift') {
        setShiftData({
          ...commonData,
          workdayStart: '08:00',
          workdayDuration: '09:00',
          breakDuration: '01:00',
          isTemplate: false
        });
        setShiftDialogVisible(true);
      } else if (type === 'absence') {
        setAbsenceData({
          ...commonData,
          from: finalDate
            .clone()
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
            .toISOString(),
          to: finalDate
            .clone()
            .set({ hour: 17, minute: 0, second: 0, millisecond: 0 })
            .toISOString(),
          absence: { id: 0, label: '', shortName: '', shouldRemoveShift: false }
        });
        setAbsenceDialogVisible(true);
      }
    },
    [selectedDateFilter, selectedDepartmentFilter?.key]
  );

  const onHideScheduleCard = useCallback(() => {
    if (calendarCardRef.current !== null) {
      calendarCardRef.current.handleCancel();
    }
  }, []);

  const onHideAbsenceCard = useCallback(() => {
    if (absenceCardRef.current !== null) {
      absenceCardRef.current.handleCancel();
    }
  }, []);

  const handleToggleExportDialogVisible = useCallback(() => {
    setExportDialogVisible((prev) => !prev);
  }, []);

  const handleToggleUnassignedTasksDialogVisible = useCallback(() => {

    setUnassignedTasksDialogVisible((prev) => !prev);
  }, []);

  const onCalculatorClick = useCallback(() => {
    setCalculatorDialogVisible(true);
  }, []);

  const handleDeleteShiftsClick = useCallback(() => {
    setDeletionDialogVisible(true);
  }, []);

  const handleDeleteAbsencesClick = useCallback(() => {
    setDeletionAbsenceDialogVisible(true);
  }, []);

  const handleMassDelete = useCallback(
    (startDate: Date, endDate: Date, selectedEmployees: CalendarAssignmentEmployee[]) => {
      // Сохраняем параметры для последующего использования
      setDeletionParams({
        startDate,
        endDate,
        selectedEmployees
      });
      // Закрываем диалог параметров и открываем диалог подтверждения
      setDeletionDialogVisible(false);
      setConfirmDeletionDialogVisible(true);
    },
    []
  );

  const handleMassDeleteAbsences = useCallback(
    (startDate: Date, endDate: Date, selectedEmployees: CalendarAssignmentEmployee[]) => {
      setDeletionAbsenceParams({
        startDate,
        endDate,
        selectedEmployees
      });
      setDeletionAbsenceDialogVisible(false);
      setConfirmAbsenceDeletionDialogVisible(true);
    },
    []
  );

  const handleCalculatorConfirm = useCallback(
    (
      startDate: Date,
      endDate: Date,
      selectedEmployees: CalendarAssignmentEmployee[],
      shouldClearBeforeRecalc = true,
      shouldDeleteAbsences = true
    ) => {
      setRecalcParams({
        startDate,
        endDate,
        selectedEmployees,
        shouldClear: shouldClearBeforeRecalc,
        shouldDeleteAbsences: shouldDeleteAbsences
      });
      setCalculatorDialogVisible(false);
      setConfirmRecalcDialogVisible(true);
    },
    []
  );

  return {
    // Common dialog refs + hide handlers
    calendarCardRef,
    absenceCardRef,
    onHideScheduleCard,
    onHideAbsenceCard,

    // Open/create helpers
    openNewDialog,

    // Task info dialog
    selectedTaskInfo,
    setSelectedTaskInfo,

    // Shift dialog
    shiftDialogVisible,
    setShiftDialogVisible,
    shiftData,
    setShiftData,

    // Absence dialog
    absenceDialogVisible,
    setAbsenceDialogVisible,
    absenceData,
    setAbsenceData,

    // Choice dialog
    choiceDialogVisible,
    setChoiceDialogVisible,
    selectedPersonId,
    setSelectedPersonId,
    selectedDate,
    setSelectedDate,

    // Validation dialogs
    validationErrors,
    setValidationErrors,
    validationDialogVisible,
    setValidationDialogVisible,
    ruleValidationErrors,
    setRuleValidationErrors,
    ruleValidationDialogVisible,
    setRuleValidationDialogVisible,

    // Import errors dialog
    validationErrorsImport,
    setValidationErrorsImport,
    errorDialogVisible,
    setErrorDialogVisible,

    // Calculator/recalc dialogs
    calculatorDialogVisible,
    setCalculatorDialogVisible,
    confirmRecalcDialogVisible,
    setConfirmRecalcDialogVisible,
    recalcParams,
    setRecalcParams,
    onCalculatorClick,
    handleCalculatorConfirm,

    // Deletion dialogs
    deletionDialogVisible,
    setDeletionDialogVisible,
    confirmDeletionDialogVisible,
    setConfirmDeletionDialogVisible,
    deletionParams,
    setDeletionParams,
    deletionAbsenceDialogVisible,
    setDeletionAbsenceDialogVisible,
    confirmAbsenceDeletionDialogVisible,
    setConfirmAbsenceDeletionDialogVisible,
    deletionAbsenceParams,
    setDeletionAbsenceParams,
    handleDeleteShiftsClick,
    handleDeleteAbsencesClick,
    handleMassDelete,
    handleMassDeleteAbsences,

    // Export dialog
    exportDialogVisible,
    setExportDialogVisible,
    handleToggleExportDialogVisible,

    // Unassigned tasks dialog
    unassignedTasksDialogVisible,
    setUnassignedTasksDialogVisible,
    handleToggleUnassignedTasksDialogVisible,

    unassignedTasksCount,
    setUnassignedTasksCount
  };
}

